import { useState } from 'react';
import { Link, useLocation/*, useNavigate*/ } from 'react-router-dom';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { Camera, Crown/*, LogOut*/, MessageCircle/*, User as LucideUser, Settings*/, Sparkles } from 'lucide-react';

// import { supabase } from '../lib/supabase';
import EhAuthDialog from './EhAuthDialog';
// import {
//   DropdownMenu,
//   DropdownMenuTrigger,
//   DropdownMenuContent,
//   DropdownMenuItem,
// } from './ui/dropdown-menu';
import { Button } from './ui/button';
import { formatDecimal } from '@/lib/utils';
import { isHidingMonetizationInStores } from '@/lib/detection';

import { useUserInfo } from '@/hooks/useUserInfo';
import { useGemBalance } from '@/hooks/useGemBalance';
import { useScreenBreakpoints } from '@/hooks/useScreenBreakpoints';
import { DEFAULT_BALANCE } from '@/contexts/GemBalanceContext';

interface EhHeaderProps {
  ref: React.RefObject<HTMLElement | null>;
  SidebarTrigger?: React.ReactNode;
}

export const EH_HEADER_HEIGHT = 57;

export function EhHeader({ ref, SidebarTrigger }: EhHeaderProps) {
  const { session, isLoading: isSessionLoading } = useSessionContext();
  // const navigate = useNavigate();
  const location = useLocation();
  const { chats, isPastUser, isSubscriber, userDetails } = useUserInfo();
  const { isMobile } = useScreenBreakpoints();
  const { balance } = useGemBalance();

  const [authOpen, setAuthOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState<string | undefined>(undefined);

  // const handleLogout = async () => {
  //   await supabase.auth.signOut();
  // };

  // Handler to open auth dialog
  const handleOpenAuth = (media?: string) => {
    setMediaUrl(media);
    setAuthOpen(true);
  };

  // const navigateToNewCharacter = () => {
  //   navigate('/create-character');
  // };
  // const navigateToSettings = () => {
  //   navigate('/settings');
  // };

  // Handler for character creation (to be implemented later)
  // const handleCreateCharacter = () => {
  //   if (session?.user) {
  //     // Navigate to character creation page
  //     console.log('Create character');
  //   } else {
  //     // Open auth dialog if not logged in
  //     handleOpenAuth();
  //   }
  // };

  const showStudioButton = (location.pathname !== '/studio' && !isMobile)
    || (location.pathname.startsWith('/c/') && window?.matchMedia('(min-width: 480px)').matches);

  const showGemBalance = balance.total !== DEFAULT_BALANCE.total;

  return (
    <>
      <header ref={ref} className={`bg-zinc-900 border-b border-zinc-800 py-2 px-2 sm:px-4 max-w-full overflow-hidden h-[${EH_HEADER_HEIGHT}px]`}>
        <div className="2xl:container mx-auto flex justify-between items-center gap-2">
          <div className="flex items-center justify-start gap-2">
            {SidebarTrigger && (
              <div>{SidebarTrigger}</div>
            )}
            <Link to="/" className="flex items-center">
              <span className="text-xl font-bold text-white">
                <span>Evening</span><span className="text-amber-500">Honey</span>
              </span>
            </Link>
          </div>
          <div className={`flex items-center justify-end gap-2`}>
            {!isSessionLoading && !session?.user && (
              <Button
                size="lg"
                className="max-md:hidden border border-amber-500 text-amber-500 bg-transparent px-2 sm:px-4 py-2 rounded-lg font-semibold hover:bg-amber-950 transition"
                onClick={() => handleOpenAuth()}
              >
                {isPastUser && <span>Login</span>}
                {!isPastUser && (
                  <span>
                    <span className="hidden lg:inline">Create </span>
                    <span>Free Account</span>
                  </span>
                )}
              </Button>
            )}
            {!!userDetails && showGemBalance && (
              <Button
                asChild
                size="lg"
                variant="outline"
                className="px-2 lg:px-3 py-2 max-[320px]:hidden border-zinc-700 bg-transparent hover:bg-blue-400/20 hover:text-white"
              >
                <Link to="/gems" className="flex items-center font-normal gap-1.5">
                  <span>💎</span>
                  <span className="text-lg text-blue-100">{formatDecimal(balance.total, 1)}</span>
                </Link>
              </Button>
            )}
            {/* Subscription button: only show when authenticated free user and has chats */}
            {!isHidingMonetizationInStores() && location.pathname !== '/subscription' && !!userDetails && chats.length > 0 && !isSubscriber && (
              <div className="relative p-px bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg max-[480px]:hidden">
                <div className="bg-zinc-800 rounded-lg hover:bg-gradient-to-r hover:from-purple-600/10 hover:to-pink-600/10 transition-colors">
                  <Button
                    asChild
                    size="lg"
                    className="relative sm:px-4 py-2 rounded-lg transition shadow-md border-0 bg-zinc-800"
                  >
                    <Link
                      to="/subscription"
                      className="-m-px flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      <Crown className="size-5 sm:size-6 text-purple-600" />
                      <span>
                        <span className="font-normal hidden lg:inline">Premium </span>
                        <span className="font-bold">70% OFF</span>
                      </span>
                    </Link>
                  </Button>
                </div>
              </div>
            )}
            {location.pathname !== '/create-character' && (
            <Button
              asChild
              size="lg"
              // Hide button on smaller screens to make room for gems balance button
              // use sizes used elswhere in max-[<XXX>px] such as 480 for CSS classes to already be included in bundle
              className={`px-2 sm:px-4 py-2 max-[${isSubscriber ? 480 : 320}px]:hidden bg-zinc-800`}
              variant="pinkOutline"
            >
              <Link to="/create-character">
                <Sparkles className="size-4 sm:size-5" />
                <span className="hidden xl:inline">Create Your AI</span>
                  <span className="xl:hidden">Create AI</span>
                </Link>
              </Button>
            )}
            <Button
              asChild
              size="lg"
              // use sizes used elswhere in max-[<XXX>px] such as 480 for CSS classes to already be included in bundle
              className={`px-2 sm:px-4 py-2 max-[${location.pathname === '/create-character' ? 320 : 480}px]:hidden bg-zinc-800 ${
                showStudioButton ? '' : 'hidden'
              }`}
              variant="pinkOutline"
            >
              <Link to="/studio">
                <Camera className="size-4 sm:size-5" />
                <span className="hidden xl:inline">Image Studio</span>
                <span className="xl:hidden">Studio</span>
                </Link>
            </Button>
            {!!userDetails && chats.length > 0 && !location.pathname.startsWith('/c/') && (
              <Button
                asChild
                size="lg"
                variant="ghost"
                className="px-1 has-[>svg]:px-1 sm:has-[>svg]:px-2 max-sm:hidden"
              >
                <Link to="/c">
                  <MessageCircle className="size-5 sm:size-6" />
                  <span className="font-normal hidden lg:inline">Chats</span>
                </Link>
              </Button>
            )}
            {/* !isSessionLoading && !!session?.user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="lg" className="px-1 has-[>svg]:px-1 sm:has-[>svg]:px-2">
                    <LucideUser className="size-5 sm:size-6" />
                    <span className="font-normal hidden md:inline">Account</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border border-zinc-700 text-white">
                  <DropdownMenuItem className="hover:bg-rose-600/10 text-md cursor-pointer min-[480px]:hidden" onClick={navigateToNewCharacter}>
                    <Sparkles className="size-4" />
                    <span>Create your AI</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="hover:bg-rose-600/10 text-md cursor-pointer" onClick={navigateToSettings}>
                    <Settings className="size-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="hover:bg-rose-600/10 text-md cursor-pointer" onClick={handleLogout}>
                    <LogOut className="size-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) */}
          </div>
        </div>
      </header>
      <EhAuthDialog
        open={authOpen}
        onOpenChange={(open) => setAuthOpen(open)}
        mediaUrl={mediaUrl}
      />
    </>
  );
}; 